import { Avatar, AvatarGroup, Box, Popover, Stack } from "@mui/material"
import { Fragment, memo, useContext, useMemo, useState } from "react"
import UserContext from "../../../../context/user/user-context"
import {
  iconColorForUserStatus,
  lastLoginFailureFromUserStatus,
  lastLoginFromUserStatus,
  loginCountFromUserStatus,
  recentLogsFromUserStatus,
  userStatusForEmail,
  userStatusForGameListItem,
} from "../../../../data-utils/userStatusUtils"
import { useQueries } from "../../../../data-utils/reactQueryHelpers.js"
import { TABLE_USER_PROFILE_LIMIT } from "../../../../utilities/constants"
import { useReplaneConfigNumber } from "../../../../utils/useReplaneConfig"
import { UiTextWrapper } from "../../../common/MuiStyled/Text"

const UserProfiles = memo(function UserProfiles({ keyName, gameData, userData, userStatusData }) {
  const userContext = useContext(UserContext)
  const [anchorEle, setAnchorEle] = useState(null)
  const [shouldFetchUserStatus, setShouldFetchUserStatus] = useState(false)

  // Extract emails for this game to lazy-load user status
  const gameEmails = useMemo(() => {
    if (!gameData[keyName] || !Array.isArray(gameData[keyName])) return []
    return gameData[keyName]
      .map((user) => {
        const userId = user?.user_id
        const userRecord = userData?.[userId] ?? null
        return userRecord?.email
      })
      .filter(Boolean) // Remove null/undefined
  }, [gameData, keyName, userData])

  // Lazy load user status only when user hovers (on-demand fetching)
  const userStatusQuery = useQueries.HOME_GAME_USER_STATUS({
    gameId: gameData?.game_id,
    emails: gameEmails,
    enabled: shouldFetchUserStatus && gameEmails.length > 0 && !gameData.isGroupRoot,
  })

  // Use lazy-loaded data if available, fallback to prop for backward compatibility
  const effectiveUserStatusData = userStatusQuery.data || userStatusData || []

  const tableUserProfileLimitCount = useReplaneConfigNumber("frontend", "ui.table_user_profile_limit", TABLE_USER_PROFILE_LIMIT)
  const isDetailPopupOpened = Boolean(anchorEle)
  const componentId = String(gameData?.game_id ?? crypto.randomUUID())
    .split(" ")
    .join("_")

  const userForId = (id) => {
    return userData?.[id] ?? userContext.userDataList?.[id] ?? null
  }

  const userStatusDataFor = (email) => {
    return userStatusForEmail({ email, statusDataList: effectiveUserStatusData })
  }

  const handleDetailPopupOpen = (event) => {
    // popup not allowed on grouped rows
    if (!gameData.isGroupRoot) {
      setAnchorEle(event.currentTarget || event.target)
      // Trigger user status fetch on first hover
      if (!shouldFetchUserStatus) {
        setShouldFetchUserStatus(true)
      }
    }
  }

  const handleDetailPopupClose = () => {
    setAnchorEle(null)
  }

  return (
    <Fragment>
      <div
        aria-owns={isDetailPopupOpened ? `${componentId}-popup` : undefined}
        aria-haspopup="true"
        onMouseEnter={handleDetailPopupOpen}
        onMouseLeave={handleDetailPopupClose}
      >
        <AvatarGroup
          id={componentId}
          max={tableUserProfileLimitCount}
          sx={{
            flexDirection: "row-reverse",
            float: "left",
            // Flattened spec: 24px, light --ct-avatar bg, dark initials, 9.5/600, 2px white ring
            "& .MuiAvatar-circular": {
              width: 24,
              height: 24,
              fontSize: "9.5px",
              fontWeight: 600,
              bgcolor: "var(--ct-avatar)",
              color: "var(--ct-ink-2)",
              border: "2px solid #fff",
            },
          }}
        >
          {gameData[keyName].map((userData, userIndex) => {
            if (userData?.isBot) {
              return (
                <Avatar
                  key={`bot-${keyName}-${userIndex}`}
                  alt={`${userData.name} (AI)`}
                  title={`${userData.name}${userData.role ? ` · ${userData.role}` : ""} (AI)`}
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: "var(--color-theme, #F6AB36)",
                    color: "#1a1205",
                    fontWeight: 700,
                    border: "2px dashed rgba(255,255,255,0.6) !important",
                    pointerEvents: "none",
                  }}
                >
                  {(userData.name?.[0] || "A").toUpperCase()}
                </Avatar>
              )
            }
            const userId = userData?.user_id
            const userRecord = userForId(userId)
            const userStatusData = userRecord ? userStatusDataFor(userRecord.email) : null
            const userStatus = userStatusForGameListItem({
              statusData: userStatusData,
              gameListItem: gameData,
            })
            const userStatusColor = iconColorForUserStatus(userStatus)

            return userRecord ? (
              <Avatar
                alt={userRecord.name || "Anonymous"}
                src={userRecord.picture || undefined}
                key={`user-${keyName}-${userIndex}`}
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: "9.5px",
                  fontWeight: 600,
                  bgcolor: "var(--ct-avatar)",
                  color: "var(--ct-ink-2)",
                  // 2px ring: white by default (overlap separator), user-status color when known
                  border: `2px solid ${userStatusColor || "#fff"} !important`,
                  pointerEvents: "none",
                }}
              >
                {userRecord.name?.[0]?.toUpperCase() || "?"}
              </Avatar>
            ) : (
              <></>
            )
          })}
        </AvatarGroup>
      </div>
      <Popover
        id={`${componentId}-popup`}
        open={isDetailPopupOpened}
        onClose={handleDetailPopupClose}
        anchorEl={anchorEle}
        anchorOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        sx={{
          pointerEvents: "none",
          "& .MuiPopover-paper": {
            borderRadius: "10px",
          },
        }}
        disableRestoreFocus
      >
        <Box
          component={"div"}
          className="user-detail-menu"
          p={"10px"}
          sx={{
            backgroundColor: "var(--ct-surface)",
            borderRadius: "10px",
          }}
        >
          {gameData[keyName].map((data, dataIndex) => {
            if (data?.isBot) {
              return (
                <Stack
                  key={`bot-${dataIndex}`}
                  direction={"row"}
                  alignItems={"center"}
                  py={"5px"}
                  gap={"15px"}
                  sx={{ "&:not(:first-child)": { borderTop: "2px solid #292929" } }}
                >
                  <Avatar
                    alt={`${data.name} (AI)`}
                    key={`bot-${keyName}-${dataIndex}`}
                    sx={{
                      width: 30,
                      height: 30,
                      bgcolor: "var(--color-theme, #F6AB36)",
                      color: "#1a1205",
                      fontWeight: 700,
                      border: "2px dashed rgba(255,255,255,0.6) !important",
                      pointerEvents: "none",
                    }}
                  >
                    {(data.name?.[0] || "A").toUpperCase()}
                  </Avatar>
                  <Stack direction={"column"} justifyContent={"center"} className="user-info" sx={{ whiteSpace: "nowrap" }}>
                    <UiTextWrapper size={16} weight={600}>
                      {data.name} (AI)
                    </UiTextWrapper>
                    {data.role ? <UiTextWrapper size={14}>{data.role}</UiTextWrapper> : <></>}
                    <UiTextWrapper size={14}>AI teammate</UiTextWrapper>
                  </Stack>
                </Stack>
              )
            }
            const userId = data.user_id
            const userRecord = userForId(userId)
            const userStatusData = userRecord ? userStatusDataFor(userRecord.email) : null
            const userStatus = userStatusForGameListItem({
              statusData: userStatusData,
              gameListItem: gameData,
            })
            const userStatusColor = iconColorForUserStatus(userStatus)
            const recentLogs = recentLogsFromUserStatus(userStatusData)
            return (
              <Stack
                key={`user-${dataIndex}`}
                direction={"row"}
                alignItems={"center"}
                py={"5px"}
                gap={"15px"}
                sx={{
                  "&:not(:first-child)": {
                    borderTop: "2px solid var(--ct-line)",
                  },
                }}
              >
                <Avatar
                  alt={userRecord?.name || "Anonymous"}
                  src={userRecord?.picture || undefined}
                  key={`user-${keyName}-${dataIndex}`}
                  sx={{
                    width: 30,
                    height: 30,
                    border: `2px solid ${userStatusColor || "transparent"} !important`,
                    pointerEvents: "none",
                  }}
                >
                  {userRecord?.name?.[0]?.toUpperCase() || "?"}
                </Avatar>
                <Stack
                  direction={"column"}
                  justifyContent={"center"}
                  className="user-info"
                  sx={{
                    whiteSpace: "nowrap",
                  }}
                >
                  {(() => {
                    // Facilitated virtual seats have a reserved @seat.virtual email that is NOT a
                    // real address — show the seat's participant name (or the role) + the role, and
                    // hide the synthetic email so the list reads "Alice — IT Lead", not
                    // "it-lead@seat.virtual" (user request 2026-09-09).
                    const seatEmail = userRecord?.email || data?.email || ""
                    if (/@seat\.virtual$/i.test(seatEmail)) {
                      const seatName = userRecord?.name || data?.name || ""
                      const seatRole = data?.role || userRecord?.role || ""
                      return (
                        <>
                          <UiTextWrapper size={16} weight={600}>
                            {seatName || seatRole || "Participant"}
                          </UiTextWrapper>
                          {seatRole && seatRole !== seatName ? <UiTextWrapper size={14}>{seatRole}</UiTextWrapper> : <></>}
                        </>
                      )
                    }
                    return (
                      <>
                        {userRecord?.name && userRecord.name !== userRecord.email ? (
                          <UiTextWrapper size={16} weight={600}>
                            {userRecord.name}
                          </UiTextWrapper>
                        ) : (
                          <></>
                        )}
                        {userRecord?.email ? <UiTextWrapper size={14}>{userRecord.email}</UiTextWrapper> : <></>}
                      </>
                    )
                  })()}
                  {userStatusData ? (
                    <UiTextWrapper size={14}>
                      Status:{" "}
                      {userStatusForGameListItem({
                        statusData: userStatusData,
                        gameListItem: gameData,
                      })}
                    </UiTextWrapper>
                  ) : (
                    <></>
                  )}
                  {userStatusData ? <UiTextWrapper size={14}>Logins: {loginCountFromUserStatus(userStatusData)}</UiTextWrapper> : <></>}
                  {userStatusData ? <UiTextWrapper size={14}>Last Login: {lastLoginFromUserStatus(userStatusData)}</UiTextWrapper> : <></>}
                  {userStatusData ? (
                    <UiTextWrapper size={14}>Last Failed Login: {lastLoginFailureFromUserStatus(userStatusData)}</UiTextWrapper>
                  ) : (
                    <></>
                  )}
                  {recentLogs && recentLogs.length > 0 ? (
                    <>
                      <UiTextWrapper size={14}>Recent Logs:</UiTextWrapper>
                      <Stack
                        p={"5px"}
                        direction={"column"}
                        justifyContent={"center"}
                        className="user-log-list"
                        sx={{
                          border: "1px solid var(--ct-line-2)",
                        }}
                      >
                        {recentLogs.map((log, index) => (
                          <Box
                            component={"span"}
                            key={`log-${index}`}
                            className="user-log"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "10px",
                              whiteSpace: "pre",
                            }}
                          >
                            {log}
                          </Box>
                        ))}
                      </Stack>
                    </>
                  ) : (
                    <></>
                  )}
                </Stack>
              </Stack>
            )
          })}
        </Box>
      </Popover>
    </Fragment>
  )
})

UserProfiles.displayName = "UserProfiles"

export default UserProfiles
